import React, { useState } from "react";
import { FaTimes, FaTools, FaStar, FaBox } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import { useToast } from "../../contexts/ToastContext";
import styles from "../../styles/components/setup/AddToSetupModal.module.css";

const STATUSES = [
  { value: "own", label: "I Own It" },
  { value: "wishlist", label: "Wishlist" },
  { value: "sold", label: "Sold" },
];

const AddToSetupModal = ({ product, existingItem, onClose, onSaved, onRemoved }) => {
  const { showToast } = useToast();

  const getProductCategoryName = (p) => {
    if (!p?.category) return "";
    if (typeof p.category === "object" && p.category?.name) return p.category.name;
    if (typeof p.category === "string") return p.category;
    return "";
  };

  const [status, setStatus] = useState(existingItem?.status || "own");
  const [category, setCategory] = useState(
    existingItem?.category || getProductCategoryName(product) || ""
  );
  const [note, setNote] = useState(existingItem?.note || "");
  const [rating, setRating] = useState(existingItem?.rating || 0);
  const [ownedSince, setOwnedSince] = useState(
    existingItem?.ownedSince
      ? new Date(existingItem.ownedSince).toISOString().split("T")[0]
      : ""
  );
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  const thumb = (() => {
    const imgs = product?.images;
    if (Array.isArray(imgs) && imgs.length) return getImageUrl(imgs[0]);
    return null;
  })();

  const handleSave = async () => {
    if (!product?._id) {
      setError("This product is no longer available. You can remove it from your Setup.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload = {
        productId: product._id,
        status,
        category: category.trim(),
        note: note.trim(),
        rating: rating || null,
        ownedSince: ownedSince || null,
      };

      const res = existingItem
        ? await api.patch(`/setup/${existingItem._id}`, payload)
        : await api.post("/setup", payload);

      showToast({ message: existingItem ? "Setup updated" : "Added to your Setup!", type: "success" });
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!existingItem) return;
    setRemoving(true);
    try {
      await api.delete(`/setup/${existingItem._id}`);
      showToast({ message: "Removed from your Setup", type: "success" });
      onRemoved();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove");
      setRemoving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            <FaTools size={14} />
            {existingItem ? "Edit Setup Item" : "Add to My Setup"}
          </span>
          <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
            <FaTimes size={14} />
          </button>
        </div>

        {/* Product strip */}
        <div className={styles.productStrip}>
          {thumb ? (
            <img src={thumb} alt={product?.name || "Product"} className={styles.productThumb} />
          ) : (
            <div className={styles.productThumbPlaceholder}>
              <FaBox size={20} />
            </div>
          )}
          <div>
            <p className={styles.productName}>{product?.name || "Product no longer available"}</p>
            {product?.brand?.name && (
              <p className={styles.productBrand}>{product.brand.name}</p>
            )}
          </div>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {/* Status pills */}
          <div className={styles.field}>
            <span className={styles.label}>Status</span>
            <div className={styles.statusRow}>
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={`${styles.statusPill} ${status === s.value ? styles.statusPillActive : ""}`}
                  onClick={() => setStatus(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div className={styles.field}>
            <label className={styles.label}>Category</label>
            <input
              className={styles.input}
              placeholder="e.g. Desk, Audio, PC, Camera…"
              value={category}
              maxLength={60}
              onChange={(e) => setCategory(e.target.value)}
            />
          </div>

          {/* Note */}
          <div className={styles.field}>
            <label className={styles.label}>Your Note (optional)</label>
            <textarea
              className={styles.textarea}
              placeholder="Why you chose it, how long you've had it…"
              value={note}
              maxLength={300}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {/* Rating */}
          <div className={styles.field}>
            <span className={styles.label}>Your Rating</span>
            <div className={styles.starRow}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${styles.star} ${n <= rating ? styles.starFilled : ""}`}
                  onClick={() => setRating(n === rating ? 0 : n)}
                  title={`${n} star${n !== 1 ? "s" : ""}`}
                >
                  <FaStar />
                </button>
              ))}
            </div>
          </div>

          {/* Owned since */}
          {status === "own" && (
            <div className={styles.field}>
              <label className={styles.label}>Owned Since</label>
              <input
                type="date"
                className={styles.input}
                value={ownedSince}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setOwnedSince(e.target.value)}
              />
            </div>
          )}
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {/* Actions */}
        <div className={styles.actions}>
          {existingItem && (
            <button
              type="button"
              className={styles.removeBtn}
              onClick={handleRemove}
              disabled={removing}
            >
              {removing ? "Removing…" : "Remove"}
            </button>
          )}
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving || !product?._id}
          >
            {saving ? "Saving…" : existingItem ? "Save Changes" : "Add to Setup"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToSetupModal;
